import { useEffect } from "react";

import { useAuthStore } from "@/lib/auth-store";
import { herstelPushAbonnement } from "@/lib/push";

/**
 * Zelfherstel van het pushabonnement na het inloggen (sessie 202610234).
 *
 * Na een wissel van de VAPID-sleutel bleef elk toestel op de oude sleutel
 * staan tot iemand op Profiel opnieuw inschreef; Apple wees elke melding
 * af (403 VapidPkHashMismatch). Deze hook draait één keer per laden van
 * het portaal per gebruiker (uitloggen en als iemand anders inloggen
 * draait opnieuw), zodra er een ingelogde gebruiker is, en laat
 * `herstelPushAbonnement` het toestel zo nodig zelf opnieuw inschrijven.
 * Nooit een toestemmingsvraag, nooit blokkerend, fouten alleen in de
 * console.
 *
 * Daarnaast luistert hij naar de service worker: na een
 * `pushsubscriptionchange` meldt die "push-abonnement-gewijzigd", en dan
 * registreert dit venster (dat wel een login heeft) het nieuwe abonnement.
 */

/** Voor welke gebruiker het herstel in dit laden al draaide. */
let gedraaidVoor: string | null = null;

/** Alleen voor tests: laat de hook opnieuw draaien. */
export function _resetPushHerstel(): void {
  gedraaidVoor = null;
}

function draaiHerstel(): void {
  herstelPushAbonnement()
    .then((uitkomst) => {
      if (uitkomst.uitkomst === "hersteld") {
        console.info("push.herstel", uitkomst.reden);
      }
    })
    .catch((fout: unknown) => {
      console.warn("push.herstel_mislukt", fout);
    });
}

export function usePushHerstel(): void {
  const ingelogd = Boolean(useAuthStore((s) => s.accessToken));
  const gebruikerId = useAuthStore((s) => s.gebruiker?.id ?? null);
  const actief = ingelogd && gebruikerId !== null;

  useEffect(() => {
    if (!actief || gedraaidVoor === gebruikerId) return;
    gedraaidVoor = gebruikerId;
    draaiHerstel();
  }, [actief, gebruikerId]);

  useEffect(() => {
    if (!actief || !("serviceWorker" in navigator)) return;
    const sw = navigator.serviceWorker;
    const opBericht = (event: MessageEvent) => {
      const data = event.data as { type?: string } | null;
      if (data?.type === "push-abonnement-gewijzigd") draaiHerstel();
    };
    sw.addEventListener("message", opBericht);
    return () => sw.removeEventListener("message", opBericht);
  }, [actief]);
}
